import { Figure, svgType } from './Figure'

/**
 * The four reconciliation states from `bill_reconciliation_state()`
 * (migration 038), and the transitions between them.
 *
 * `unposted` is drawn as the terminal branch on purpose: it is the only state
 * that is a problem, and the only one the user has to do something about.
 */
export function ReconciliationDiagram() {
  const box = (x: number, y: number, w = 132, h = 46) => ({ x, y, width: w, height: h, rx: 4 })

  return (
    <Figure
      title="Bill reconciliation state machine: none, to pending when marked paid, then to matched when a transaction is found or unposted when the grace window elapses."
      caption="States are computed from transaction_id, marked_paid_manually, paid_on and reconcile_grace_days every time they are read. Nothing stores the state, so nothing can go stale."
    >
      <svg viewBox="0 0 720 230" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <marker id="doc-recon-arrow" viewBox="0 0 10 10" refX="9" refY="5"
            markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0 0 L10 5 L0 10 z" fill="var(--text-faint)" />
          </marker>
        </defs>

        {/* none */}
        <rect {...box(0, 76)} fill="none" stroke="var(--border)" strokeWidth="1" />
        <text x="14" y="98" {...svgType.numSmall} fill="var(--text)">none</text>
        <text x="14" y="114" {...svgType.faint}>bill exists, unpaid</text>

        {/* pending */}
        <rect {...box(226, 76)} fill="none" stroke="var(--warn)" strokeWidth="1.25" />
        <text x="240" y="98" {...svgType.numSmall} fill="var(--warn-text)">pending</text>
        <text x="240" y="114" {...svgType.faint}>inside grace window</text>

        {/* matched */}
        <rect {...box(492, 20)} fill="none" stroke="var(--inflow)" strokeWidth="1.25" />
        <text x="506" y="42" {...svgType.numSmall} fill="var(--inflow-text)">matched</text>
        <text x="506" y="58" {...svgType.faint}>bank confirms it</text>

        {/* unposted */}
        <rect {...box(492, 132)} fill="none" stroke="var(--danger-text)" strokeWidth="1.25" />
        <text x="506" y="154" {...svgType.numSmall} fill="var(--danger-text)">unposted</text>
        <text x="506" y="170" {...svgType.faint}>needs a human</text>

        <g stroke="var(--text-faint)" strokeWidth="1.1" fill="none" markerEnd="url(#doc-recon-arrow)">
          {/* none -> pending */}
          <line x1="132" y1="99" x2="218" y2="99" />
          {/* pending -> matched */}
          <path d="M358 90 Q 425 90 425 48 L484 48" />
          {/* pending -> unposted */}
          <path d="M358 108 Q 425 108 425 155 L484 155" />
          {/* none -> matched, straight past pending: auto-import found it first */}
          <path d="M66 70 Q 66 8 130 8 L470 8 Q 484 8 484 20" />
        </g>

        <text x="140" y="88" {...svgType.faint}>you mark it paid</text>
        <text x="374" y="70" {...svgType.faint}>a transaction matches</text>
        <text x="372" y="126" {...svgType.faint}>grace days elapse,</text>
        <text x="372" y="140" {...svgType.faint}>still nothing</text>
        <text x="140" y="20" {...svgType.faint}>imported transaction matched it before you said anything</text>

        <line x1="0" y1="200" x2="720" y2="200" stroke="var(--border)" strokeWidth="1" />
        <text x="0" y="222" {...svgType.faint}>
          Grace window is per bill &mdash; reconcile_grace_days, three days by default.
        </text>
      </svg>
    </Figure>
  )
}
